<template>
  <v-layout
    id="charity-form"
    justify-center
    wrap
  >
    <v-flex
      xs12
      md8
      lg8
    >
      <h2 v-if="!charity">
        {{ $t('modules.charities.charityNew') }}
      </h2>
      <h2 v-else>
        {{ $t('modules.charities.charityEdit') }}
      </h2>
      <v-card class="pt-7 pl-5 pr-5">
        <v-form>
          <v-layout wrap>
            <v-flex
              mt-4
              xs12
            >
              <div
                class="image"
                @click.stop="$refs.image.click()"
              >
                <div
                  v-if="!form.image"
                  class="pt-5 pb-7"
                >
                  <v-icon color="primary">
                    mdi-cloud-upload
                  </v-icon>
                  <p>
                    {{ $t('modules.courseUploadImage') }}
                  </p>
                  <p>PNG / JPG</p>
                </div>
                <div class="image-preview">
                  <img
                    v-if="selectedImg || form.image"
                    style="display: block;"
                    :src="selectedImg || form.image.url"
                    width="300"
                    height="150"
                  >
                </div>
                <input
                  ref="image"
                  type="file"
                  style="display: none"
                  accept="image/*"
                  @input="setImage"
                >
              </div>
              <div class="text-center mt-2">
                <v-btn
                  v-if="form.image"
                  class="ml-2"
                  outlined
                  small
                  text
                  @click="form.image = null; selectedImg= null;"
                >
                  <v-icon>
                    mdi-bucket-outline
                  </v-icon>
                </v-btn>
              </div>
            </v-flex>
            <v-flex
              xs12
            >
              <v-text-field
                v-model.trim="form.name"
                :error-messages="NameErrors"
                :label="$t('modules.charities.name')+ '*'"
                required
                @input="$v.form.name.$touch()"
                @blur="$v.form.name.$touch()"
              />
            </v-flex>
            <v-flex
              xs12
              md6
            >
              <v-select
                v-model.trim="form.status"
                :items="statuses"
                :item-text="'text'"
                :item-value="'id'"
                :error-messages="StatusErrors"
                :label="$t('modules.charities.status')+'*'"
                @change="$v.form.status.$touch()"
                @blur="$v.form.status.$touch()"
              />
            </v-flex>
            <v-flex
              xs12
              md6
            >
              <v-text-field
                v-model.trim="form.needed_score"
                :error-messages="NeededScoreErrors"
                :label="$t('modules.charities.neededScore')+'*'"
                @input="$v.form.needed_score.$touch()"
                @blur="$v.form.needed_score.$touch()"
              />
            </v-flex>
            <v-flex
              xs12
            >
              <b>{{ $t("modules.charities.description") }}</b>
            </v-flex>
            <v-flex>
              <vue-editor
                id="editor"
                v-model="form.description"
                :editor-toolbar="customToolbar"
              />
            </v-flex>
            <v-flex
              xs12
              class="px-0"
            >
              <v-divider />
            </v-flex> 
          </v-layout>
          <v-layout>
            <v-flex
              xs12
              md12
            >
              <v-btn
                fab
                outlined
                small
                text
                @click="$emit('closeEditMode', false)"
              >
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <v-btn
                color="primary"
                style="float:right"
                :disabled="loading || $v.$invalid"
                :loading="loading"
                @click="save()"
              >
                {{ $t('globals.saveButton') }}
              </v-btn>
            </v-flex>
          </v-layout>
        </v-form>
      </v-card>
    </v-flex>
  </v-layout>
</template>

<script>
import CHARITY_CREATE_MUTATION from "@/graphql/mutations/modules/game/CharityCreate.gql"
import CHARITY_UPDATE_MUTATION from "@/graphql/mutations/modules/game/CharityUpdate.gql";
import { validationMixin } from "vuelidate";
import {
  required,
  minLength,
  maxLength,
  numeric,
  integer,
  minValue,
  maxValue,
} from "vuelidate/lib/validators";
import { VueEditor } from "vue2-editor";

export default {
  name: "ModuleGameCharityEditForm",
  components: {
    VueEditor
  },
  mixins: [validationMixin],
  props: {
    charity: {
      type: Object,
      default: null
    },
    whitelabelUuid: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      selectedImg: null,
      statuses: [
        {id: "pending", text: this.$t('modules.charities.pending')},
        {id: "active", text: this.$t('modules.charities.active')},
        {id: "archived", text: this.$t('modules.charities.archived')}
      ],
      customToolbar: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'script': 'sub'}, { 'script': 'super' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        [{ 'direction': 'rtl' }],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': []}, 'link'],

        ['clean']
      ],
      form: {}
    }
  },
  computed: {
    rules() {
      return {
        name: {
          required,
          minLength: minLength(3),
          maxLength: maxLength(100),
        },
        status: {
         required,
        },
        needed_score: {
          required,
          numeric,
          integer,
          minValue: minValue(1),
          maxValue: maxValue(999999999)
        },
      }
    },
    NameErrors() {
      const errors = [];
      if (!this.$v.form.name.$dirty) return errors;
      !this.$v.form.name.required && errors.push(this.$t("globals.reqField"));
      !this.$v.form.name.minLength &&
        errors.push(this.$t("whitelabel.minError", {char: this.$v.form.name.$params.minLength.min}));
      !this.$v.form.name.maxLength &&
        errors.push(this.$t("whitelabel.maxError", {char: this.$v.form.name.$params.maxLength.max}));
      return errors;
    },
    StatusErrors() {
      const errors = [];
      if (!this.$v.form.status.$dirty) return errors;
      !this.$v.form.status.required && errors.push(this.$t("globals.reqField"));
      return errors;
    },
    NeededScoreErrors() {
      const errors = [];
      if (!this.$v.form.needed_score.$dirty) return errors;
      !this.$v.form.needed_score.required && errors.push(this.$t("globals.reqField"));
      !this.$v.form.needed_score.numeric && errors.push(this.$t("globals.numberField"));
      !this.$v.form.needed_score.integer && errors.push(this.$t("globals.numberField"));
      !this.$v.form.needed_score.minValue &&
        errors.push(this.$t("whitelabel.minValueError", {char: this.$v.form.needed_score.$params.minValue.min}));
      !this.$v.form.needed_score.maxValue &&
        errors.push(this.$t("whitelabel.maxValueError", {char: this.$v.form.needed_score.$params.maxValue.max}));
      return errors;
    },
  },
  mounted() {
    const form = JSON.parse(JSON.stringify(this.charity));
    if (form) {      
      this.form = form
    } else {
      this.form = {
        image: null,
        name: "",
        status: "",
        needed_score: null,
        description: "",
      }
    }
  },
  validations() {
    return {
      form: this.rules
    };
  },
  methods: { 
    setImage(e) {
      this.selectedImg = URL.createObjectURL(e.target.files[0])
      this.form.image = e.target.files[0];
    },
    save() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return
      }
      this.loading = true;
      if(!this.form.image || this.form.image.hasOwnProperty('uuid')) {
        delete this.form.image;
      }

      if(this.charity) this.form.charity_uuid = this.charity.uuid;

      this.form.whitelabel_uuid = this.whitelabelUuid;
      this.form.needed_score = +this.form.needed_score;

      this.$apollo
        .mutate({
          mutation: !this.charity ? CHARITY_CREATE_MUTATION : CHARITY_UPDATE_MUTATION,
          variables: this.form
        })
        .then(data => {
          this.loading = false;
          this.$emit('closeEditMode', true)
        })
        .catch(error => {
          this.loading = false;
          this.errorHandler(error);
        });
    },
  }
}
</script>

<style lang="scss">
#charity-form {
  .image {
    width: 300px;
    margin: 0 auto;
    border: 1px solid #d2d2d2;
    border-radius: 5px;
    text-align: center;
    cursor: pointer;
    min-height: 150px;
    max-height: 150px;
    .image-preview {
      width: 300;
      overflow: hidden;
      border-radius: 5px;
    }
    i {
      margin: 5px 0 0px;
      font-size: 26px;
    }
    p {
      margin: 7px 0 0!important;
      &:first-of-type {
        font-weight: 700;
      }
      line-height: 0.85rem;
    }
    small {
      font-size: 11px;
    }
  }
}
</style>
